/**
 * Volgende stap van een workflow-pane (besluit Asil, 2 oktober 2026: na Excel-tests zag een collega niet
 * genoeg wanneer hij door kon naar de volgende stap; de staat in de menukop en het groene vinkje op de
 * knop waren niet genoeg).
 *
 * Zuivere hulpfuncties zonder React, zodat de engine-tests ze kunnen draaien. De pane geeft zijn stappen
 * in de volgorde van de actiebalk, elk met de staat van de knop en de statustekst van het menu
 * (`menuStatusText`); `pickNextStep` kiest daaruit wat de regel boven de balk noemt en `stepMarks`
 * welke knop de huidige stap is en welke later komen.
 */

/**
 * Staat van een stap (codex-review GEM-02, 27 september 2026): "ready" = gereed om te starten
 * (de gebruiker moet nog iets doen), "active" alleen tijdens een lopende handeling, "stale" =
 * opnieuw nodig of nog niet bijgewerkt na een wijziging.
 */
export type ActionState = "todo" | "ready" | "active" | "done" | "stale" | "error";

export interface WorkflowStep<Id extends string = string> {
  id: Id;
  /** Naam in de regel, gelijk aan de menutitel ("2 · Opzet"). */
  label: string;
  state: ActionState;
  /** Wat er te doen is, of (klaar) wat er gedaan is: de statustekst van het menu. */
  text?: string;
  /** Eigen kopwoord bij stale/error ("Nog niet bijgewerkt", "Nakijken"); anders het standaardwoord. */
  head?: string;
  /** Geen verplichte stap: alleen genoemd als de verplichte stappen klaar zijn (SmartTie Dekking, Rapport). */
  optional?: boolean;
  /**
   * Nog niet klaar, maar het kan wachten tot de latere verplichte stappen klaar zijn (Sampling: de
   * evaluatie is voorlopig tot het akkoord in Dossier). Wordt pas genoemd als die er niet meer zijn.
   */
  deferred?: boolean;
  /**
   * Optionele stap die, zolang hij niet klaar is, de groene "Klaar" tegenhoudt (SmartTie Dekking met niet
   * onderzochte tabellen; besluit Asil 4 oktober 2026). De tekst volgt op "Klaar, ": "op 3 niet-onderzochte
   * tabellen na". De regel wordt dan "Klaar, op 3 … na — bekijk Dekking", niet groen; niets wordt geblokkeerd.
   */
  caveat?: string;
}

export type NextStepKind = "next" | "stale" | "error" | "busy" | "done" | "optional" | "partial";

export interface NextStep<Id extends string = string> {
  kind: NextStepKind;
  /** De stap waar de regel naar wijst (bij "busy" zonder stap: er loopt een handeling buiten de stappen). */
  step?: WorkflowStep<Id>;
  /** Bij "done": de eerste optionele stap die nog open is, als suggestie. */
  optional?: WorkflowStep<Id>;
  /** Kopwoord van de regel: "Volgende stap", "Opnieuw nodig", "Klaar" … */
  head: string;
  /** Wat erachter staat: de stap en wat er te doen is, of wat er gedaan is. */
  detail: string;
}

export type StepLang = "NL" | "EN";

const HEADS: Record<StepLang, Record<NextStepKind, string>> = {
  NL: {
    next: "Volgende stap",
    stale: "Opnieuw nodig",
    error: "Let op",
    busy: "Bezig",
    done: "Klaar",
    optional: "Optioneel",
    partial: "Klaar",
  },
  EN: {
    next: "Next step",
    stale: "Needs redoing",
    error: "Attention",
    busy: "Working",
    done: "Done",
    optional: "Optional",
    partial: "Done",
  },
};
/** Losse woorden van de regel (Engels sinds de Store-voorbereiding, 5 oktober 2026). */
const WORDS: Record<StepLang, { view: string; finished: string; optional: string }> = {
  NL: { view: "bekijk", finished: "afgerond", optional: "optioneel" },
  EN: { view: "view", finished: "finished", optional: "optional" },
};

const lower = (s: string): string => s.charAt(0).toLowerCase() + s.slice(1);
const withText = (step: WorkflowStep, text?: string): string =>
  text ? `${step.label} — ${lower(text)}` : step.label;

function fromStep<Id extends string>(
  step: WorkflowStep<Id>,
  optional = false,
  lang: StepLang = "NL"
): NextStep<Id> {
  const kind: NextStepKind =
    step.state === "active"
      ? "busy"
      : step.state === "stale"
        ? "stale"
        : step.state === "error"
          ? "error"
          : optional
            ? "optional"
            : "next";
  const head = (kind === "stale" || kind === "error") && step.head ? step.head : HEADS[lang][kind];
  // Geen kopwoord twee keer ("Bijwerken nodig: Dossier — bijwerken nodig: gewijzigd …").
  const text =
    step.text && step.text.toLowerCase().indexOf(head.toLowerCase()) === 0
      ? step.text.slice(head.length).replace(/^[\s:;,·—-]+/, "")
      : step.text;
  return { kind, step, head, detail: withText(step, text) };
}

/**
 * Kiest de volgende stap: (1) een lopende handeling (`busy`, de fase in woorden) of een stap die bezig
 * is; (2) de eerste verplichte stap die niet klaar is en niet kan wachten; (3) een verplichte stap die
 * kon wachten; (4) alles klaar: de laatste verplichte stap met wat er gedaan is, plus de eerste
 * optionele stap die nog open staat. Geen stappen = null.
 */
export function pickNextStep<Id extends string>(
  steps: ReadonlyArray<WorkflowStep<Id>>,
  busy?: string | null,
  lang: StepLang = "NL"
): NextStep<Id> | null {
  const H = HEADS[lang === "EN" ? "EN" : "NL"];
  const W = WORDS[lang === "EN" ? "EN" : "NL"];
  const active = steps.filter((s) => s.state === "active")[0];
  if (busy || active) {
    const detail = busy || (active && active.text) || (active ? active.label : "");
    return {
      kind: "busy",
      step: active,
      head: H.busy,
      detail: detail.replace(/…$|\.\.\.$/, "") + "…",
    };
  }
  const required = steps.filter((s) => !s.optional);
  const open = required.filter((s) => s.state !== "done");
  const first = open.filter((s) => !s.deferred)[0] ?? open[0];
  if (first) return fromStep(first, false, lang);
  // Klaar met een voorbehoud: een optionele stap met `caveat` die nog niet klaar is (niet groen, wel klikbaar).
  const caveat = steps.filter((s) => s.optional && s.caveat && s.state !== "done")[0];
  if (caveat) {
    return {
      kind: "partial",
      step: caveat,
      head: `${H.partial}, ${caveat.caveat}`,
      detail: `${W.view} ${caveat.label.replace(/^\d+\s*·\s*/, "")}`,
    };
  }
  const optional = steps.filter((s) => s.optional && s.state !== "done" && s.state !== "todo")[0];
  const last = required[required.length - 1];
  if (!last) return optional ? fromStep(optional, true, lang) : null;
  const doneText = last.text
    ? lower(last.text)
    : `${lower(last.label.replace(/^\d+\s*·\s*/, ""))} ${W.finished}`;
  return {
    kind: "done",
    step: last,
    optional,
    head: H.done,
    detail: optional
      ? `${doneText} · ${W.optional}: ${optional.label.replace(/^\d+\s*·\s*/, "")}`
      : doneText,
  };
}

/** De volledige regel als tekst, voor tooltip en toegankelijke naam. */
export function nextStepText(next: NextStep | null): string {
  if (!next) return "";
  return `${next.head}${nextStepSeparator(next)}${next.detail}`;
}

/** Scheidingsteken tussen kopwoord en detail: " — " bij klaar (ook met voorbehoud), anders ": ". */
export function nextStepSeparator(next: NextStep): string {
  return next.kind === "done" || next.kind === "partial" ? " — " : ": ";
}

/** Het menu dat een klik op de regel opent (bij klaar de optionele suggestie, anders de laatste stap). */
export function nextStepTarget<Id extends string>(next: NextStep<Id> | null): Id | null {
  if (!next || next.kind === "busy") return null;
  if (next.kind === "done") return (next.optional ?? next.step)?.id ?? null;
  return next.step?.id ?? null;
}

export type StepMark = "current" | "later";

/**
 * Markering per knop: "current" = de stap waar de regel naar wijst; "later" = een stap die na de huidige
 * komt en nog niet klaar is (gedempt, nooit uitgeschakeld). Klare stappen houden hun vinkje, zonder markering.
 */
export function stepMarks<Id extends string>(
  steps: ReadonlyArray<WorkflowStep<Id>>,
  next: NextStep<Id> | null
): Partial<Record<Id, StepMark>> {
  const marks: Partial<Record<Id, StepMark>> = {};
  if (!next || next.kind === "done" || !next.step) return marks;
  // Klaar met voorbehoud: alleen de stap van het voorbehoud is aan de beurt; de rest is klaar of optioneel.
  if (next.kind === "partial") {
    marks[next.step.id] = "current";
    return marks;
  }
  const at = steps.map((s) => s.id).indexOf(next.step.id);
  if (at < 0) return marks;
  marks[next.step.id] = "current";
  steps.forEach((s, i) => {
    if (i > at && s.state !== "done") marks[s.id] = "later";
  });
  return marks;
}
